import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { isProduction } from '@/libraries/signup/bxpkit-core/src/utils/environment';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { useAnalyticsClient } from '../../../../../../../src/analytics/wac-analytics/analytics-client';
import { useLocalizedHref } from '../../../../../../libraries/web-framework/src/localization/use-localized-href';

import { LoginContactSales } from './components/login-contact-sales';
import { LoadingSpinner } from './components/spinner';

export const SoftwareRovoStartPageTemplate: React.FC = () => {
	const stargateClient = useBxpWebStargateClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const { isUserLoggedIn, requestInProgress } = useLoggedInUser();

	const [redirectUrl, setRedirectUrl] = useState<string>();

	const localizedContactHref = useLocalizedHref('/enterprise/contact');

	const adminBaseUrl = useMemo(() => {
		if (typeof window === 'undefined') {
			return 'https://admin.atlassian.com';
		}
		return isProduction() ? 'https://admin.atlassian.com' : 'https://admin.stg.atlassian.com';
	}, []);

	useEffect(() => {
		redirectUrl && setTimeout(() => (window.location.href = redirectUrl), 300);
	}, [redirectUrl]);

	const fireViewAnalytics = useCallback(
		(attributes: { [key: string]: boolean } = {}) => {
			analyticsClient &&
				createAnalyticsEvent({
					action: 'viewed',
					eventType: 'screen',
					name: 'softwareRovoStartPage',
					source: 'softwareRovoStartPage',
					attributes,
				}).fire();
		},
		[analyticsClient, createAnalyticsEvent],
	);

	const getIsOrgAdmin = useCallback(async () => {
		const { success, accountOrgs } = await stargateClient.getAccountOrgs({});

		if (!success) {
			throw new Error('Get account/orgs call failed');
		}

		const isOrgAdmin = !!(accountOrgs && accountOrgs.length > 0);

		fireViewAnalytics({ isOrgAdmin });

		isOrgAdmin ? setRedirectUrl(`${adminBaseUrl}/rovo`) : setRedirectUrl(localizedContactHref);
	}, [fireViewAnalytics, stargateClient, setRedirectUrl, localizedContactHref, adminBaseUrl]);

	const checkIfRovoProductExists = useCallback(async () => {
		const productsUrl = isProduction()
			? 'https://rovo.atlassian.com'
			: 'https://home.stg.atlassian.com/rovo';

		const { accountProducts, success } = await stargateClient.getAccountProducts({});

		if (!success) {
			throw new Error('Get account/products call failed');
		}

		if (accountProducts && accountProducts.length > 0) {
			const siteWithRovo = accountProducts.find((site) => site.products.includes('rovo.ondemand'));
			if (siteWithRovo) {
				fireViewAnalytics({ hasRovo: true });

				const cloudId = siteWithRovo.cloudId;
				setRedirectUrl(`${productsUrl}?cloudId=${cloudId}`);
				return;
			}
		}

		getIsOrgAdmin();
	}, [stargateClient, fireViewAnalytics, getIsOrgAdmin, setRedirectUrl]);

	useEffect(() => {
		switch (requestInProgress) {
			case false:
				switch (isUserLoggedIn) {
					case true:
						checkIfRovoProductExists();
						break;
					case false:
						fireViewAnalytics();
						break;
					default:
						break;
				}
				break;
			default:
				break;
		}
	}, [fireViewAnalytics, checkIfRovoProductExists, isUserLoggedIn, requestInProgress]);

	return <>{!requestInProgress && !isUserLoggedIn ? <LoginContactSales /> : <LoadingSpinner />}</>;
};
